import React, { useState } from 'react';
import InfoPopover from '../InfoPopover';
import './MetricsRow.css';

/**
 * MetricsRow - Compact horizontal display of key performance metrics
 *
 * Design: Value on top (large), label below (small)
 * Primary metrics: Total Return, Max Drawdown, Annualized Return (CAGR)
 * Secondary metrics (hidden by default): Sharpe, Sortino, ROMAD
 *
 * Props: metrics (metric values), compact (smaller sizing), showToggle (the
 * "More Stats" button), showSecondary (show Sharpe, Sortino, ROMAD when there
 * is no toggle), size, windowDays.
 *
 * @param {{
 *   metrics?: Record<string, any>,
 *   compact?: boolean,
 *   showToggle?: boolean,
 *   showSecondary?: boolean,
 *   size?: string,
 *   windowDays?: number | null,
 * }} props
 */
export default function MetricsRow({
  metrics = {},
  compact = false,
  showToggle = true,
  showSecondary = false,
  size = 'default',
  windowDays = null,
}) {
  const [expanded, setExpanded] = useState(false);

  const formatMoney = (value) => {
    const number = Number(value);
    return Number.isFinite(number)
      ? number.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
      : null;
  };

  const metricExplanations = {
    totalReturn: {
      meaning:
        'The percentage change in account value over this exact backtest window (ending vs starting cash).',
      formula: '(ending value − starting value) ÷ starting value',
    },
    maxDrawdown: {
      meaning: 'The largest peak-to-trough decline in the equity curve during this run.',
      formula: '(trough value − prior peak value) ÷ prior peak value',
    },
    annualReturn: {
      meaning: 'The compounded yearly rate implied by the return over this backtest window.',
      formula: '(ending value ÷ starting value)^(365 ÷ elapsed days) − 1',
    },
    sharpe: {
      meaning:
        'Return earned for each unit of volatility. Higher means steadier gains for the ups and downs taken. Above 1 is generally good.',
      formula: 'average excess return ÷ standard deviation of returns (annualized)',
    },
    sortino: {
      meaning:
        'Like Sharpe, but only counts downside swings, so upside jumps are not punished. Higher is better.',
      formula: 'average excess return ÷ standard deviation of negative returns (annualized)',
    },
    romad: {
      meaning:
        'Return over max drawdown: how much the strategy earned for the worst drop it took. Higher is better.',
      formula: 'annualized return ÷ |max drawdown|',
    },
  };

  const metricPopover = (key, label) => {
    const detail = metrics.metricDetails?.[key] || {};
    const startValue = formatMoney(detail.startValue);
    const endValue = formatMoney(detail.endValue);
    const startDate = detail.startDate ? new Date(detail.startDate) : null;
    const endDate = detail.endDate ? new Date(detail.endDate) : null;
    const elapsedDays =
      startDate && endDate && !Number.isNaN(startDate.getTime()) && !Number.isNaN(endDate.getTime())
        ? Math.max(0, Math.round((endDate.getTime() - startDate.getTime()) / 86_400_000))
        : null;
    return (
      <InfoPopover label={label}>
        <p>{metricExplanations[key].meaning}</p>
        <p>
          <strong>Formula:</strong> <code>{metricExplanations[key].formula}</code>
        </p>
        {(startValue || endValue || detail.startDate || detail.endDate) && (
          <p>
            <strong>This run:</strong>{' '}
            {[startValue, detail.startDate, endValue, detail.endDate].filter(Boolean).join(' · ')}
          </p>
        )}
        {detail.sourceArtifact && (
          <p>
            <strong>Source:</strong> {detail.sourceArtifact}
          </p>
        )}
        {key === 'totalReturn' &&
          detail.cashFlowAdjustedReturn !== undefined &&
          detail.cashFlowAdjustedReturn !== null && (
            <p>
              <strong>Cash-flow-adjusted (secondary):</strong>{' '}
              {formatPercent(detail.cashFlowAdjustedReturn)}
              {detail.cashFlowAdjustedSourceArtifact
                ? ` · ${detail.cashFlowAdjustedSourceArtifact}`
                : ''}
              . This compounds period returns after removing deposits/withdrawals and can differ
              from the balance change above.
            </p>
          )}
        {key === 'annualReturn' && elapsedDays !== null && elapsedDays < 365 && (
          <p className="info-popover__warning">
            This is a short window ({elapsedDays} days). Annualizing compounds that result
            hypothetically; it is not a forecast.
          </p>
        )}
      </InfoPopover>
    );
  };

  // Format percentage: 0.1523 -> "+15.23%", 49.9168 -> "+4,991.68%"
  const formatPercent = (value) => {
    if (value === null || value === undefined) {
      return 'N/A';
    }
    // Convert to number in case it's a string from API
    const numValue = Number.parseFloat(String(value));
    if (isNaN(numValue)) {
      return 'N/A';
    }
    const pct = numValue * 100;
    const sign = pct >= 0 ? '+' : '-';
    // Use toLocaleString for thousands separator (e.g., 4991.68 -> 4,991.68)
    const formatted = Math.abs(pct).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return `${sign}${formatted}%`;
  };

  // Format ratio: 1.85 -> "1.85"
  const formatRatio = (value) => {
    if (value === null || value === undefined) {
      return 'N/A';
    }
    // Convert to number in case it's a string from API
    const numValue = Number.parseFloat(String(value));
    if (isNaN(numValue)) {
      return 'N/A';
    }
    return numValue.toFixed(2);
  };

  // Get value class for coloring
  const getValueClass = (value, alwaysNegative = false) => {
    if (value === null || value === undefined) {
      return '';
    }
    // Convert to number in case it's a string from API
    const numValue = Number.parseFloat(String(value));
    if (isNaN(numValue)) {
      return '';
    }
    if (alwaysNegative) return numValue === 0 ? 'neutral' : 'negative';
    return numValue >= 0 ? 'positive' : 'negative';
  };

  const primaryMetrics = [
    {
      label: 'Total Return',
      key: 'totalReturn',
      value: formatPercent(metrics.totalReturn),
      className: getValueClass(metrics.totalReturn),
    },
    {
      label: 'Max Drawdown',
      key: 'maxDrawdown',
      value: formatPercent(metrics.maxDrawdown),
      className: getValueClass(metrics.maxDrawdown, true),
    },
    {
      label: 'Annualized Return (CAGR)',
      key: 'annualReturn',
      value: formatPercent(metrics.annualReturn),
      className: getValueClass(metrics.annualReturn),
    },
  ];

  const secondaryMetrics = [
    {
      label: 'Sharpe',
      key: 'sharpe',
      value: formatRatio(metrics.sharpe),
      className: getValueClass(metrics.sharpe),
    },
    {
      label: 'Sortino',
      key: 'sortino',
      value: formatRatio(metrics.sortino),
      className: getValueClass(metrics.sortino),
    },
    {
      label: 'ROMAD',
      key: 'romad',
      value: formatRatio(metrics.romad),
      className: getValueClass(metrics.romad),
    },
  ];

  const shouldShowSecondary = showToggle ? expanded : showSecondary;
  const shortWindowDays =
    windowDays !== null && Number.isFinite(Number(windowDays)) && Number(windowDays) < 365
      ? Math.max(0, Math.round(Number(windowDays)))
      : null;

  return (
    <div
      className={[
        'metrics-row',
        compact ? 'metrics-row--compact' : '',
        size !== 'default' ? `metrics-row--${size}` : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="metrics-row__primary">
        {primaryMetrics.map((metric) => (
          <div key={metric.label} className="metrics-row__metric">
            <span className={`metrics-row__value ${metric.className}`}>{metric.value}</span>
            <span className="metrics-row__label">
              <span className="metrics-row__label-text">{metric.label}</span>
              {metricPopover(metric.key, metric.label)}
            </span>
            {metric.key === 'annualReturn' && shortWindowDays !== null && (
              <span className="metrics-row__warning">Short {shortWindowDays}-day test</span>
            )}
          </div>
        ))}
      </div>

      {shouldShowSecondary && (
        <div className="metrics-row__secondary">
          {secondaryMetrics.map((metric) => (
            <div key={metric.label} className="metrics-row__metric metrics-row__metric--secondary">
              <span className={`metrics-row__value ${metric.className}`}>{metric.value}</span>
              <span className="metrics-row__label">
                <span className="metrics-row__label-text">{metric.label}</span>
                {metricPopover(metric.key, metric.label)}
              </span>
            </div>
          ))}
        </div>
      )}

      {showToggle && (
        <button
          className="metrics-row__toggle"
          onClick={() => setExpanded(!expanded)}
          aria-expanded={expanded}
        >
          {expanded ? 'Less' : 'More Stats'}
        </button>
      )}
    </div>
  );
}
